import { FC, useState, useEffect, MouseEvent } from 'react';
import { api } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { Icon } from './icons/Icon';

interface LikeButtonProps {
    submissionId: string;
    initialCount?: number;
    authorId?: string;       // 作品の作者ID（自分の作品にはいいね不可）
    myId?: string | null;    // 自分のID（未ログインなら null）
    isLoggedIn?: boolean;
    initialLiked?: boolean;
    size?: 'sm' | 'md';
    className?: string;
}

// 作品に直接いいねできるボタン。1ユーザー1回（押すと♥が満ちる一方向トグル）。
export const LikeButton: FC<LikeButtonProps> = ({
    submissionId, initialCount = 0, authorId, myId, isLoggedIn = false, initialLiked = false, size = 'md', className = '',
}) => {
    const [count, setCount] = useState(initialCount);
    const [liked, setLiked] = useState(!!initialLiked);
    useEffect(() => { if (initialLiked) setLiked(true); }, [initialLiked]);
    const [busy, setBusy] = useState(false);
    const { playSound } = useGamenics();

    const isSelf = !!authorId && !!myId && authorId === myId;
    const pad = size === 'sm' ? 'px-2.5 py-1 text-[12px]' : 'px-3.5 py-1.5 text-sm';
    const iconSize = size === 'sm' ? 14 : 17;

    const handleClick = async (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (busy || liked) return;

        if (!isLoggedIn) {
            toast({ title: 'ログインするといいねできます', description: '右上のログインから参加しよう' });
            return;
        }
        if (isSelf) {
            toast({ title: '自分の作品にはいいねできません' });
            return;
        }

        setBusy(true);
        setLiked(true); // 楽観的にハートを満たす
        playSound(GamenicsEvent.TAP_CONFIRM);

        const res = await api.like(submissionId);
        if (res.success) {
            if (!res.alreadyLiked) setCount((c) => c + 1);
        } else {
            setLiked(false); // 失敗時はロールバック
            if (res.code === 'SELF_VOTE_FORBIDDEN') {
                toast({ title: '自分の作品にはいいねできません' });
            } else if (res.code === 'UNAUTHORIZED') {
                toast({ title: 'ログインするといいねできます' });
            } else {
                toast({ title: 'いいねできませんでした', description: res.error });
            }
        }
        setBusy(false);
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={busy}
            aria-pressed={liked}
            aria-label="いいね"
            className={`inline-flex items-center gap-1.5 font-display font-bold rounded-full border-2 border-ink transition-transform active:scale-95 disabled:opacity-60 ${pad} ${
                liked ? 'bg-pop text-white shadow-hard-sm' : 'bg-surface text-pop hover:-translate-y-0.5'
            } ${className}`}
        >
            <Icon name="heart" size={iconSize} className={liked ? 'fill-current' : ''} />
            {count.toLocaleString()}
        </button>
    );
};
